'use client';

import { Fragment } from 'react';
import Link from 'components/shared/Link/Link';
import { cn, getColorway } from 'utils/styles';
import { Colorways } from 'types/constants';
import useBreadcrumbs from './useBreadcrumbs';
import Schema from '@/components/shared/Schema/Schema';

export type BreadcrumbsClassNames = {
  outerWrapper: string;
  innerWrapper: string;
  listItem: string;
  baseItem: string;
  interactive: string;
};

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbsProps {
  pageTitle?: string;
  breadcrumbItems?: Array<BreadcrumbItem>;
  truncateTitle?: boolean;
  classNames?: Partial<BreadcrumbsClassNames>;
}

const Breadcrumbs = ({ pageTitle, breadcrumbItems = [], truncateTitle = false, classNames }: BreadcrumbsProps) => {
  const { crumbs, schema } = useBreadcrumbs({ pageTitle, breadcrumbItems });

  const internalClassNames: BreadcrumbsClassNames = {
    outerWrapper: cn('py-4', getColorway(Colorways.Primary), classNames?.outerWrapper),
    innerWrapper: cn(
      'content-wrapper flex flex-wrap items-center gap-x-4 gap-y-2 font-sans',
      { 'flex-nowrap': truncateTitle },
      classNames?.innerWrapper,
    ),
    listItem: cn({ 'max-w-fit flex-1 truncate': truncateTitle }, classNames?.listItem),
    baseItem: cn('first-letter:uppercase font-semibold', classNames?.baseItem),
    interactive: cn('hover:text-primary hover:underline shrink-0', classNames?.interactive),
  };

  const homeAddress = crumbs[0]?.href ?? '/';
  return (
    <nav aria-label="Breadcrumb" className={internalClassNames.outerWrapper} role="navigation">
      <ol className={internalClassNames.innerWrapper}>
        <li className="flex">
          <Link className="flex-inline h-6 w-6 flex items-center justify-center" href={homeAddress}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="16"
              viewBox="0 0 14 16"
              fill="none"
              className="-mt-0.5">
              <path
                d="M13.1468 5.99326L12.1199 4.96208V2.02852C12.1199 1.68199 11.8467 1.40132 11.51 1.40132H10.4951C10.1581 1.40132 9.88516 1.68231 9.88516 2.02852V2.71738L7.49513 0.317578C7.07354 -0.105859 6.40046 -0.105859 5.97887 0.317578L3.36858 2.93867L0.326918 5.99326C0.118019 6.20319 0 6.491 0 6.79146V14.8845C0 15.5006 0.485643 16 1.08489 16H4.86243C5.07164 16 5.24141 15.8254 5.24141 15.6103V11.5544C5.24141 11.2887 5.45094 11.0732 5.70939 11.0732H7.7643C8.02274 11.0732 8.23227 11.2887 8.23227 11.5544V15.6077C8.23227 15.8241 8.40299 15.9997 8.61346 15.9997H12.3888C12.988 15.9997 13.4737 15.5003 13.4737 14.8841V6.79146C13.4737 6.491 13.3557 6.20319 13.1468 5.99326Z"
                fill="#1EC677"
              />
            </svg>
          </Link>
        </li>
        {crumbs.slice(1).map((crumb, index) => {
          // skip the first item because it's the home link
          return (
            <Fragment key={crumb.href ?? `${index}-crumb`}>
              <li className="font-bold" aria-hidden="true">
                ›
              </li>
              <li className={internalClassNames.listItem}>
                {crumb.href ? (
                  <Link
                    href={crumb.href}
                    className={cn(internalClassNames.baseItem, internalClassNames.interactive)}
                    title={crumb.label}>
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={cn('text-content-secondary', internalClassNames.baseItem)} aria-current="page">
                    {crumb.label}
                  </span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
      <Schema jsonLd={schema} />
    </nav>
  );
};

export default Breadcrumbs;
